import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  computed,
  signal,
  effect,
  untracked,
} from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { ContextMenuService } from '@core/services/context-menu.service';
import { KingUser, KingHit } from '../../interfaces/king.interface';
import { KingNewService } from '../../king-new.service';
import { sleep } from '@core/utils/sleep.util';
import { animateElement } from '@core/utils/deprecated.utils';
import BezierEasing from 'bezier-easing';

// ============================================================================
// Types & Constants
// ============================================================================

/** Size configuration for the avatar circle */
interface AvatarSizeConfig {
  svgSize: number;
  center: number;
  radius: number;
  strokeWidth: number;
}

/** Queued hit with pre-calculated values */
interface QueuedHit {
  data: KingHit;
  hpBeforePercent: number;
  hpBefore: number;
}

/** Animation timing constants (in milliseconds) */
const ANIMATION_TIMING = {
  INDICATOR_DELAY: 300,
  HP_ANIMATION_DURATION: 500,
  INDICATOR_DISPLAY_TIME: 1500,
} as const;

/** Size configurations for different avatar sizes */
const SIZE_CONFIG: Record<1 | 2, AvatarSizeConfig> = {
  1: { svgSize: 140, center: 70, radius: 67, strokeWidth: 5 },
  2: { svgSize: 54, center: 27, radius: 25, strokeWidth: 2 },
} as const;

// ============================================================================
// Component
// ============================================================================

@Component({
  selector: 'app-king-user-avatar',
  imports: [DecimalPipe],
  templateUrl: './king-user-avatar.component.html',
  styleUrl: './king-user-avatar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  host: {
    '[class.king]': '!isChallenger()',
    '[class.challenger]': 'isChallenger()',
  },
})
export class KingUserAvatarComponent {
  // ============================================================================
  // Injected Services
  // ============================================================================

  public readonly contextMenuService = inject(ContextMenuService);
  private readonly kingService = inject(KingNewService);

  // ============================================================================
  // Component Inputs
  // ============================================================================

  readonly size = input<1 | 2>(1);
  readonly kingUser = input<KingUser | null>(null);
  readonly isChallenger = input<boolean>(false);
  readonly isWinner = input<boolean>(false);
  readonly disableAnimation = input<boolean>(false);

  // ============================================================================
  // Derived State
  // ============================================================================

  /** User role derived from isChallenger */
  readonly role = computed<'king' | 'challenger'>(() =>
    this.isChallenger() ? 'challenger' : 'king'
  );

  /** Fallback avatar based on role */
  readonly fallbackAvatar = computed(() =>
    this.isChallenger()
      ? 'assets/icons/king-new/user.svg'
      : 'assets/icons/king-new/king.svg'
  );

  // ============================================================================
  // SVG Circle Configuration
  // ============================================================================

  private readonly sizeConfig = computed(() => SIZE_CONFIG[this.size()]);

  readonly svgSize = computed(() => this.sizeConfig().svgSize);
  readonly svgViewBox = computed(() => `0 0 ${this.svgSize()} ${this.svgSize()}`);
  readonly circleCenter = computed(() => this.sizeConfig().center);
  readonly circleRadius = computed(() => this.sizeConfig().radius);
  readonly circleStrokeWidth = computed(() => this.sizeConfig().strokeWidth);
  readonly circleCircumference = computed(() => 2 * Math.PI * this.circleRadius());

  // ============================================================================
  // HP State & Calculations
  // ============================================================================

  /** Current HP percentage (0-100) */
  readonly hpPercent = computed(() => {
    const user = this.kingUser();
    if (!user || !user.initial_hp) return 100;
    const hp = Math.max(0, user.hp ?? 0);
    return this.clampPercent((hp / user.initial_hp) * 100);
  });

  /** Stroke dashoffset for HP circle based on current/animated HP */
  readonly strokeDashoffset = computed(() => {
    const percent = this.isAnimation() ? this.animatedHpPercent() : this.hpPercent();
    const circumference = this.circleCircumference();
    return circumference - (percent / 100) * circumference;
  });

  /** Display HP value (animated or actual) */
  readonly displayHp = computed(() => {
    if (this.isAnimation()) {
      return this.animatedHp();
    }
    return this.kingUser()?.hp ?? 0;
  });

  // ============================================================================
  // Damage Arc Calculations
  // ============================================================================

  /** Damage arc signal (percentage of damage to show) */
  readonly damageArcPercent = signal<number>(0);

  /** SVG dasharray for damage arc */
  readonly damageArcDasharray = computed(() => {
    const circumference = this.circleCircumference();
    const damageLength = (this.damageArcPercent() / 100) * circumference;
    return `${damageLength} ${circumference - damageLength}`;
  });

  /** SVG dashoffset for damage arc positioning */
  readonly damageArcDashoffset = computed(() => {
    const circumference = this.circleCircumference();
    const hpPercent = this.animatedHpPercent();
    const damagePercent = this.damageArcPercent();
    const startPosition = hpPercent - damagePercent;
    return -(startPosition / 100) * circumference;
  });

  // ============================================================================
  // Animation State
  // ============================================================================

  readonly isAnimation = signal<boolean>(false);
  readonly isHit = signal<boolean>(false);
  readonly animatedHpPercent = signal<number>(100);
  readonly animatedHp = signal<number>(0);

  // Hit indicator signals
  readonly showCritical = signal<boolean>(false);
  readonly showMiss = signal<boolean>(false);
  readonly showDamage = signal<boolean>(false);
  readonly criticalDamage = signal<number>(0);
  readonly lastDamage = signal<number>(0);

  // Private animation tracking
  private isAnimating = false;
  private isProcessingQueue = false;
  private lastProcessedHitIndex = -1;
  private initializedUserId: string | null = null;
  private initializedHp: number | null = null;

  // Damage display queue — see KingNewService JSDoc for the 3-queue architecture overview.
  private hitQueue: QueuedHit[] = [];

  // ============================================================================
  // Constructor & Effects
  // ============================================================================

  constructor() {
    this.setupUserSyncEffect();
    this.setupHitAnimationEffect();
  }

  /**
   * Sync animated values with actual HP on user load/change or new game.
   * Only re-initializes when user changes or HP resets (new game).
   */
  private setupUserSyncEffect(): void {
    effect(() => {
      const user = this.kingUser();
      const hpPercent = this.hpPercent();
      const currentHitCount = this.kingService.hits().length;

      untracked(() => {
        if (user && user.initial_hp) {
          const isNewUser = user.steamid !== this.initializedUserId;
          const isNewGame = user.initial_hp !== this.initializedHp;

          if (isNewUser || isNewGame) {
            this.initializedUserId = user.steamid;
            this.initializedHp = user.initial_hp;
            this.lastProcessedHitIndex = currentHitCount;
            this.animatedHpPercent.set(hpPercent);
            this.animatedHp.set(user.hp ?? 0);
            // Clear any pending state from previous game
            this.resetAllState();
          }
        } else if (!user) {
          this.initializedUserId = null;
          this.initializedHp = null;
          this.lastProcessedHitIndex = -1;
          // Full reset when no user
          this.resetAllState();
          this.animatedHpPercent.set(100);
          this.animatedHp.set(0);
        }
      });
    });
  }

  /**
   * Handle incoming hit events and trigger animations.
   * Queues hits and processes them in order.
   * Skips if disableAnimation is true.
   */
  private setupHitAnimationEffect(): void {
    effect(() => {
      const hits = this.kingService.hits();
      const hitCount = hits.length;
      const lastHit = hits.at(-1);
      const role = this.role();
      const user = this.kingUser();
      const disabled = this.disableAnimation();

      untracked(() => {
        if (disabled) return;

        if (
          lastHit &&
          hitCount > this.lastProcessedHitIndex &&
          lastHit.by !== role &&
          user
        ) {
          this.lastProcessedHitIndex = hitCount;
          const hpBeforePercent = lastHit.hp_before;
          const hpBefore = (hpBeforePercent / 100) * (user.initial_hp ?? 0);

          // Queue the hit
          this.hitQueue.push({
            data: lastHit,
            hpBeforePercent,
            hpBefore,
          });

          // Start processing if not already processing queue
          if (!this.isProcessingQueue) {
            this.isProcessingQueue = true;
            this.processNextHit();
          }
        }
      });
    });
  }

  // ============================================================================
  // Animation Methods
  // ============================================================================

  /**
   * Process the next hit in the queue.
   */
  private async processNextHit(): Promise<void> {
    const hit = this.hitQueue.shift();
    if (!hit) {
      this.isProcessingQueue = false;
      return;
    }

    await this.scheduleHitAnimation(hit.data, hit.hpBeforePercent, hit.hpBefore);
  }

  /**
   * Schedule a hit animation after the weapon animation delay.
   */
  private async scheduleHitAnimation(
    data: KingHit,
    hpBeforePercent: number,
    hpBefore: number
  ): Promise<void> {
    const { GUN_ANIMATION_DURATION } = this.kingService;
    await sleep(GUN_ANIMATION_DURATION - 2000);
    await this.hitAnimation(data, hpBeforePercent, hpBefore);

    // After animation completes, process next hit in queue or stop processing
    if (this.hitQueue.length > 0) {
      this.processNextHit();
    } else {
      this.isProcessingQueue = false;
    }
  }

  /**
   * Execute the hit animation sequence.
   * Shows damage indicators, animates HP bar, and displays results.
   * Uses hit data directly to avoid race conditions with service updates.
   */
  private async hitAnimation(
    data: KingHit,
    hpBeforePercent: number,
    hpBefore: number
  ): Promise<void> {
    if (this.isAnimating) return;
    this.isAnimating = true;

    // Handle miss (zero damage)
    if (data.damage === 0) {
      await sleep(ANIMATION_TIMING.INDICATOR_DELAY);
      await this.showMissIndicator();
      this.isAnimating = false;
      return;
    }

    const user = this.kingUser();
    const initialHp = user?.initial_hp ?? 1;

    // We read hp_now from the hit event payload rather than user?.hp because
    // KingNewService updates user.hp on its own timer (the HP state queue).
    // By the time this animation runs, the service may have already applied
    // a later hit's HP value, making user.hp stale for *this* animation.
    // The hit payload's hp_now is the authoritative "HP after this hit"
    // from the server, so it's always correct regardless of queue timing.
    const targetPercent = this.clampPercent(data.hp_now ?? 0);
    const targetHp = (targetPercent / 100) * initialHp;
    const clampedBeforePercent = this.clampPercent(hpBeforePercent);
    const damagePercent = Math.max(0, clampedBeforePercent - targetPercent);

    // Set damage indicator values
    this.lastDamage.set(data.damage);
    this.criticalDamage.set(data.crit ? data.damage : 0);

    // Set HP to "before" state and show damage arc
    this.animatedHpPercent.set(clampedBeforePercent);
    this.animatedHp.set(Math.floor(hpBefore));
    this.damageArcPercent.set(damagePercent);

    // Enable animation mode
    this.isAnimation.set(true);
    this.isHit.set(true);
    this.showDamage.set(true);

    // Show critical hit indicator
    if (data.crit) {
      await sleep(ANIMATION_TIMING.INDICATOR_DELAY);
      this.showCritical.set(true);
    }

    // Wait to show damage arc before animating
    await sleep(ANIMATION_TIMING.INDICATOR_DELAY);

    // Animate HP decrease
    if (damagePercent > 0) {
      const hpDifference = hpBefore - targetHp;

      await animateElement(
        (progress: number) => {
          this.damageArcPercent.set(Math.max(0, damagePercent * (1 - progress)));
          this.animatedHpPercent.set(
            this.clampPercent(clampedBeforePercent - damagePercent * progress)
          );
          this.animatedHp.set(
            Math.floor(Math.max(0, hpBefore - hpDifference * progress))
          );
        },
        ANIMATION_TIMING.HP_ANIMATION_DURATION,
        BezierEasing(0, 0, 0, 0)
      );
    }

    // Ensure final values match target from hit data
    this.animatedHpPercent.set(targetPercent);
    this.animatedHp.set(Math.floor(targetHp));
    this.damageArcPercent.set(0);

    await sleep(ANIMATION_TIMING.INDICATOR_DISPLAY_TIME);

    // Sync with actual HP before switching off animation
    this.animatedHpPercent.set(this.hpPercent());
    this.animatedHp.set(this.kingUser()?.hp ?? 0);

    this.resetAnimationState();
  }

  /**
   * Reset all animation-related state.
   */
  private resetAnimationState(): void {
    this.isHit.set(false);
    this.isAnimation.set(false);
    this.showDamage.set(false);
    this.showCritical.set(false);
    this.isAnimating = false;
  }

  /**
   * Full reset of all state - used when user changes or leaves.
   */
  private resetAllState(): void {
    this.hitQueue = [];
    this.isProcessingQueue = false;
    this.isAnimating = false;
    this.isAnimation.set(false);
    this.isHit.set(false);
    this.showCritical.set(false);
    this.showMiss.set(false);
    this.showDamage.set(false);
    this.damageArcPercent.set(0);
    this.criticalDamage.set(0);
    this.lastDamage.set(0);
  }

  /**
   * Show the miss indicator for a set duration.
   */
  private async showMissIndicator(): Promise<void> {
    this.showMiss.set(true);
    await sleep(ANIMATION_TIMING.INDICATOR_DISPLAY_TIME);
    this.showMiss.set(false);
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  /** Clamp a percentage value between 0 and 100 */
  private clampPercent(value: number): number {
    return Math.max(0, Math.min(100, value));
  }
}
